import {
  Box,
  Button,
  Collapse,
  Drawer,
  Flex,
  Grid,
  MultiSelect,
  Select,
  Text,
  TextInput,
  UnstyledButton,
} from "@mantine/core";
import { GoChevronDown } from "react-icons/go";
import {
  LuCalendarDays,
  LuCheck,
  LuDownload,
  LuFileText,
  LuInfo,
  LuSettings,
  LuUsers,
  LuX,
} from "react-icons/lu";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { useEffect, useState } from "react";
import DateRangePicker from "./DateRangePicker";
import ReportPreviewPane from "./ReportPreviewPane";
import useStyles from "./style";
import { useSelector } from "react-redux";
import { https } from "../../http";
import { showError } from "../../utils";

const NoteReportDrawer = ({ drawerOpen, drawerClose }) => {
  const { classes, cx } = useStyles();
  const [customError, setCustomError] = useState({});
  const { allPatients } = useSelector((state) => state?.patient);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const { allFacilities } = useSelector((state) => state?.facility);
  const { allNotesTypes } = useSelector((state) => state?.note);
  const { allStaff } = useSelector((state) => state?.staff);
  const { profile } = useSelector((state) => state?.profile);
  const [period, setPeriod] = useState([]);
  const [opened, { open, close }] = useDisclosure(false);
  const [advOpened, { toggle: toggleAdv, close: closeAdv }] =
    useDisclosure(false);
  const isStaffRole = profile?.user?.role === "staff";

  // step: 'form' -> 'preview' (real generated PDF) -> 'done' (confirmation + re-download)
  const [step, setStep] = useState("form");
  const [generatedFiles, setGeneratedFiles] = useState([]); // [{ blob, url, filename, label }]
  const [noteCount, setNoteCount] = useState(0);
  const [downloadedAgain, setDownloadedAgain] = useState(false);

  // Revoke the previous batch of object URLs whenever generatedFiles is
  // replaced (new report generated, or drawer reset/closed) and on unmount.
  useEffect(() => {
    return () => {
      generatedFiles.forEach((f) => URL.revokeObjectURL(f.url));
    };
  }, [generatedFiles]);

  const form = useForm({
    initialValues: {
      client: "",
      facility: "",
      noteTypes: [],
      shift: "",
      staffIds: [],
      reportTitle: "",
      groupBy: "client",
      sortOrder: "newest",
      includeSignatures: "on",
      includeApprovalTrail: "off",
    },
    validate: {
      facility: (value) => (value ? null : "Required"),
      noteTypes: (value) => (value?.length > 0 ? null : "Required"),
    },
  });
  const [dateRange, setDateRange] = useState({
    type: null,
    date: [],
  });

  const isFormValid =
    form.values.facility &&
    form.values.noteTypes.length > 0 &&
    period[0] &&
    period[1];

  const facilityName =
    allFacilities?.facilities?.find(
      (facility) => facility.id === form.values.facility,
    )?.name || "";

  const clientOptions = allPatients
    ? allPatients
        ?.filter(
          (p) =>
            p?.active === true &&
            p?.deleted === false &&
            p?.facilityId?.some(
              (f) =>
                f?.id === form.values.facility &&
                f?.active === true &&
                f?.deleted === false,
            ),
        )
        ?.map((opt) => ({ value: opt?.id, label: opt?.name }))
    : [];

  const noteTypeOptions = allNotesTypes?.ntypes
    ? allNotesTypes?.ntypes
        ?.filter((opt) => opt?.name?.toLowerCase() !== "billing services")
        ?.map((opt) => ({ value: opt?.id, label: opt?.name }))
    : [];

  const staffOptions = allStaff
    ? allStaff
        ?.filter((s) => s?.active && !s?.deleted && s?.type !== "contractor")
        ?.map((s) => ({ value: s?.id, label: s?.name }))
    : [];

  const handleApply = () => {
    if (dateRange.date?.[0] && dateRange.date?.[1]) {
      setPeriod(dateRange.date);
      // A new range invalidates the "no notes found" message from the last try.
      setCustomError((prev) => ({ ...prev, period: "", notFound: "" }));
      close();
    }
  };

  const resetAndClose = () => {
    setStep("form");
    setGeneratedFiles([]);
    setNoteCount(0);
    setDownloadedAgain(false);
    setPeriod([]);
    form.reset();
    setCustomError((prev) => ({ ...prev, period: "", notFound: "" }));
    setDateRange({
      type: null,
      date: [],
    });
    closeAdv();

    drawerClose();
  };

  const handleCreateAnother = () => {
    setStep("form");
    setGeneratedFiles([]);
    setNoteCount(0);
    setDownloadedAgain(false);
    setPeriod([]);
    form.reset();
    setCustomError((prev) => ({ ...prev, period: "", notFound: "" }));
    setDateRange({ type: null, date: [] });
    closeAdv();
  };

  // "7/1/2026_to_7/29/2026" → "7_1_2026_to_7_29_2026". toLocaleDateString()
  // emits slashes, which a browser silently rewrites to underscores in the
  // download filename anyway — done explicitly here so the saved name is what
  // this code actually chose rather than whatever the browser sanitizes it to.
  const reportPeriodFilePart = () =>
    `${period[0].toLocaleDateString()}_to_${period[1].toLocaleDateString()}`.replace(
      /\//g,
      "_",
    );

  // Generic multi-note report → "Note_Report_<facility>_<period>".
  const reportFileBaseName = () =>
    `Note_Report_${facilityName}_${reportPeriodFilePart()}`;

  // Incident Report file → "Incident_Report_<facility>_<period>". Mirrors the
  // backend's own Incident_Report_<facility> naming for this PDF
  // (generateIncidentReportPDF), which underscores every non-alphanumeric
  // character in the facility name.
  const incidentFileBaseName = () =>
    `Incident_Report_${String(facilityName || "Report").replace(
      /[^a-z0-9_-]/gi,
      "_",
    )}_${reportPeriodFilePart()}`;

  // Client(s) is optional in the UI ("Leave empty to include every client at
  // the selected facility") — the backend still requires a non-empty
  // clientId list, so an empty selection is expanded to every client at the
  // chosen facility only for the outgoing request; the MultiSelect itself
  // stays empty on screen.
  const effectiveClientIds = (values) =>
    values?.client?.length ? values.client : clientOptions.map((o) => o.value);

  const requestReportBlob = async (values, noteTypesForRequest) => {
    // The backend now uploads the generated PDF to S3 and returns a presigned
    // URL instead of streaming the binary. This is required because the API
    // Gateway in front of the API caps responses at 10 MB — large reports
    // exceeded that and failed with a 413. We download the PDF directly from
    // S3, which bypasses the gateway's size limit.
    const response = await https().post("/note/generatePdfReport", {
      facilityId: values?.facility,
      clientId: effectiveClientIds(values),
      noteTypes: noteTypesForRequest,
      shift: values?.shift || null,
      staffIds:
        !isStaffRole && values?.staffIds?.length ? values.staffIds : undefined,
      startDate: period[0].toISOString(),
      endDate: period[1].toISOString(),
      facilityName,
      reportPeriod: `${period[0].toLocaleDateString()} - ${period[1].toLocaleDateString()}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      reportTitle: values?.reportTitle || undefined,
      groupBy: values?.groupBy,
      sortOrder: values?.sortOrder,
      includeSignatures: values?.includeSignatures === "on",
      includeApprovalTrail: values?.includeApprovalTrail === "on",
    });
    const { url, count } = response.data || {};
    if (!url) throw new Error("Report URL missing from server response");
    const pdfResponse = await fetch(url, { mode: "cors" });
    if (!pdfResponse.ok)
      throw new Error(`PDF download failed: ${pdfResponse.status}`);
    // Build the Blob from the raw bytes with an explicit application/pdf type.
    // Relying on pdfResponse.blob() inherits the type from the S3 response's
    // Content-Type header — if that isn't exactly "application/pdf", the
    // <embed> preview / viewer fails with "Failed to load PDF document".
    const arrayBuffer = await pdfResponse.arrayBuffer();
    const blob = new Blob([arrayBuffer], { type: "application/pdf" });
    return { blob, count: count || 0 };
  };

  // Custom-HTML preview data (JSON) for the report — lets the preview screen
  // render a pixel-matched replica of the demo instead of embedding the real
  // PDF. Requested for EVERY note type, Incident Report included: it used to be
  // skipped for incident reports, which made the preview screen show a raw PDF
  // <embed> for that half and the styled HTML replica for the other, so one
  // report rendered two visually different previews.
  const requestReportPreview = async (values, noteTypesForRequest) => {
    const response = await https().post("/note/reportPreviewData", {
      facilityId: values?.facility,
      clientId: effectiveClientIds(values),
      noteTypes: noteTypesForRequest,
      shift: values?.shift || null,
      staffIds:
        !isStaffRole && values?.staffIds?.length ? values.staffIds : undefined,
      startDate: period[0].toISOString(),
      endDate: period[1].toISOString(),
      facilityName,
      reportPeriod: `${period[0].toLocaleDateString()} - ${period[1].toLocaleDateString()}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      reportTitle: values?.reportTitle || undefined,
      groupBy: values?.groupBy,
      sortOrder: values?.sortOrder,
      includeSignatures: values?.includeSignatures === "on",
      includeApprovalTrail: values?.includeApprovalTrail === "on",
    });
    return response.data;
  };

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleSubmit = async (values) => {
    const errors = {};
    if (!period[0] || !period[1]) errors.period = "Required";
    if (Object.keys(errors).length) {
      setCustomError(errors);
      return;
    }
    setCustomError((prev) => ({ ...prev, notFound: "" }));

    setPdfGenerating(true);
    try {
      // Split the selection by note-type NAME across EVERY matching type, not by
      // looking up a single id: NType has no unique index on `name`, so an org
      // can legitimately have more than one note type called "Incident Report"
      // (a general/system one plus its own). The backend decides to render the
      // dedicated incident PDF with
      //   NType.findOne({ _id: { $in: noteTypes }, name: 'Incident Report' })
      // i.e. by name across ALL ids it receives — so a second incident type left
      // in the "other" bucket made the backend return the INCIDENT report for
      // that half too: it downloaded as `..._Other.pdf` and silently dropped
      // every genuine other note type. Exact `===` match on purpose, to stay in
      // lockstep with that backend query.
      const incidentTypeIds = new Set(
        (allNotesTypes?.ntypes || [])
          .filter((t) => t?.name === "Incident Report")
          .map((t) => t?.id),
      );

      const selectedNoteTypes = values?.noteTypes || [];
      const incidentNoteTypes = selectedNoteTypes.filter((id) =>
        incidentTypeIds.has(id),
      );
      const otherNoteTypes = selectedNoteTypes.filter(
        (id) => !incidentTypeIds.has(id),
      );
      const hasIncidentReport = incidentNoteTypes.length > 0;

      let files;
      let totalCount;

      // The DOWNLOAD is still split in two when both kinds are selected —
      // Incident Report has its own richly-styled PDF that can't be merged with
      // the generic report — but both halves now share the same custom-HTML
      // preview, so the preview screen reads as one consistent document.
      if (hasIncidentReport && otherNoteTypes.length > 0) {
        const [incident, other, incidentPreview, otherPreview] = await Promise.all([
          requestReportBlob(values, incidentNoteTypes),
          requestReportBlob(values, otherNoteTypes),
          requestReportPreview(values, incidentNoteTypes),
          requestReportPreview(values, otherNoteTypes),
        ]);
        // Each half is its own PDF, so keep only the halves that actually
        // matched notes — otherwise a populated report drags an empty
        // companion file down with it.
        files = [
          incident.count && {
            blob: incident.blob,
            filename: `${incidentFileBaseName()}.pdf`,
            label: "Incident Report",
            previewKind: "custom",
            previewData: incidentPreview,
          },
          other.count && {
            blob: other.blob,
            filename: `${reportFileBaseName()}.pdf`,
            label: "Note Report",
            previewKind: "custom",
            previewData: otherPreview,
          },
        ].filter(Boolean);
        totalCount = incident.count + other.count;
      } else {
        // Single PDF — an Incident-Report-only selection takes this path too now
        // that it has a custom preview like every other note type.
        const [result, preview] = await Promise.all([
          requestReportBlob(values, selectedNoteTypes),
          requestReportPreview(values, selectedNoteTypes),
        ]);
        files = [
          {
            blob: result.blob,
            // Name the file after what it actually contains. An
            // Incident-Report-only selection returns the dedicated incident PDF,
            // so the generic "Note_Report_….pdf" / "Notes Report" labelling made
            // the download look like an ordinary notes report.
            filename: hasIncidentReport
              ? `${incidentFileBaseName()}.pdf`
              : `${reportFileBaseName()}.pdf`,
            label: hasIncidentReport ? "Incident Report" : "Notes Report",
            previewKind: "custom",
            previewData: preview,
          },
        ];
        totalCount = result.count;
      }

      // Nothing matched the filters — the backend still returns a (header-only)
      // PDF, so stay on the form and say so instead of handing the user an
      // empty report to preview and download.
      if (!totalCount) {
        setCustomError((prev) => ({
          ...prev,
          notFound: `No notes found between ${period[0].toLocaleDateString()} and ${period[1].toLocaleDateString()} for the selected filters. Try a different date range or different filters.`,
        }));
        return;
      }

      setGeneratedFiles(files.map((f) => ({ ...f, url: URL.createObjectURL(f.blob) })));
      setNoteCount(totalCount);
      setStep("preview");
    } catch (error) {
      console.error("Note report error:", error);
      showError("Failed to generate report. Please try again.");
    } finally {
      setPdfGenerating(false);
    }
  };

  const handleDownload = () => {
    generatedFiles.forEach((f) => downloadBlob(f.blob, f.filename));
    setDownloadedAgain(false);
    setStep("done");
  };

  const handleDownloadAgain = () => {
    generatedFiles.forEach((f) => downloadBlob(f.blob, f.filename));
    setDownloadedAgain(true);
  };

  const SelectAllLink = ({ onClick }) => (
    <UnstyledButton
      type="button"
      onClick={onClick}
      className={classes.selectAllLink}
    >
      Select all
    </UnstyledButton>
  );

  const OffOnToggle = ({ value, onChange }) => (
    <Flex className={classes.segToggle}>
      {["off", "on"].map((v) => (
        <UnstyledButton
          key={v}
          type="button"
          onClick={() => onChange(v)}
          className={cx(classes.segToggleBtn, value === v && classes.segToggleBtnActive)}
        >
          {v === "off" ? "Off" : "On"}
        </UnstyledButton>
      ))}
    </Flex>
  );

  const drawerTitle =
    step === "form" ? "Create Report" : step === "preview" ? "Report Preview" : "Report Ready";

  return (
    <Drawer
      opened={drawerOpen}
      offset={8}
      size="55%"
      sx={{
        ".mantine-Drawer-close": {
          color: "var(--app-text)",
          "& svg": {
            width: "25px !important",
            height: "25px !important",
          },
        },
        ".mantine-11shxvp": {
          background: "var(--app-bg)",
          borderTopLeftRadius: 16,
          borderBottomLeftRadius: 16,
        },
        ".mantine-Drawer-close:hover": {
          color: "var(--app-primary)",
        },
        ".mantine-1q36a81": {
          padding: "0",
          display: "flex",
          flexDirection: "column",
          height: "90%",
          overflow: "hidden",
        },
      }}
      zIndex={1000}
      title={
        <Text fz={24} fw={700} ff="Inter" color="var(--app-text)">
          {drawerTitle}
        </Text>
      }
      position="right"
      overlayProps={{ backgroundOpacity: 2.5, blur: 4 }}
      shadow="md"
      onClose={resetAndClose}
      centered
    >
      <form
        onSubmit={form.onSubmit((values) => handleSubmit(values))}
        style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}
      >
        <Box style={{ flex: 1, overflowY: "auto", minHeight: 0 }} py={14} px={24}>
        {step === "form" && (
          <>
            {/* ── Scope ─────────────────────────────────────────────────────── */}
            <Box className={classes.sectionCard}>
              <Flex align="center" gap={8} className={classes.sectionHeader}>
                <LuUsers size={16} />
                <Text>Scope</Text>
              </Flex>
              <Grid gutter={24}>
                <Grid.Col span={12}>
                  <Select
                    size="sm"
                    className={classes.inputField}
                    searchable
                    withAsterisk
                    label="Facility"
                    placeholder="Select Facility"
                    rightSectionPointerEvents="none"
                    rightSection={<GoChevronDown color="#94A3B8" size={16} />}
                    {...form.getInputProps("facility")}
                    onChange={(val) => {
                      form.setFieldValue("client", "");
                      form.setFieldValue("facility", val);
                    }}
                    data={
                      allFacilities && allFacilities?.facilities
                        ? allFacilities?.facilities
                            ?.filter(({ active, deleted }) => active && !deleted)
                            ?.map((opt) => ({
                              value: opt?.id,
                              label: opt?.name,
                            }))
                        : []
                    }
                  />
                </Grid.Col>
                <Grid.Col span={12}>
                  <Flex align="center" justify="space-between">
                    <Text className={classes.fieldLabel}>Client(s)</Text>
                    <SelectAllLink
                      onClick={() =>
                        form.setFieldValue(
                          "client",
                          clientOptions.map((o) => o.value),
                        )
                      }
                    />
                  </Flex>
                  <MultiSelect
                    searchable
                    disabled={!form.values.facility}
                    multiple
                    size="sm"
                    className={cx(classes.inputField, classes.multiSelect)}
                    rightSection={<GoChevronDown color="#94A3B8" size={16} />}
                    placeholder="Select Client"
                    rightSectionPointerEvents="none"
                    data={clientOptions}
                    value={form.values.client}
                    onChange={(val) => form.setFieldValue("client", val)}
                    error={form.errors.client}
                  />
                  <Text className={classes.subtext}>
                    Leave empty to include every client at the selected
                    facility.
                  </Text>
                </Grid.Col>
                <Grid.Col span={12}>
                  <Flex align="center" justify="space-between">
                    <Text className={classes.fieldLabel}>
                      Note Type(s) <span style={{ color: "red" }}>*</span>
                    </Text>
                    <SelectAllLink
                      onClick={() =>
                        form.setFieldValue(
                          "noteTypes",
                          noteTypeOptions.map((o) => o.value),
                        )
                      }
                    />
                  </Flex>
                  <MultiSelect
                    searchable
                    multiple
                    size="sm"
                    className={cx(classes.inputField, classes.multiSelect)}
                    rightSection={<GoChevronDown color="#94A3B8" size={16} />}
                    placeholder="Select Note Type"
                    rightSectionPointerEvents="none"
                    data={noteTypeOptions}
                    value={form.values.noteTypes}
                    onChange={(val) => form.setFieldValue("noteTypes", val)}
                    error={form.errors.noteTypes}
                  />
                </Grid.Col>
                {!isStaffRole && (
                  <Grid.Col span={6}>
                    <Flex align="center" justify="space-between">
                      <Text className={classes.fieldLabel}>Staff</Text>
                      <SelectAllLink
                        onClick={() =>
                          form.setFieldValue(
                            "staffIds",
                            staffOptions.map((o) => o.value),
                          )
                        }
                      />
                    </Flex>
                    <MultiSelect
                      searchable
                      multiple
                      size="sm"
                      className={cx(classes.inputField, classes.multiSelect)}
                      rightSection={<GoChevronDown color="#94A3B8" size={16} />}
                      placeholder="Select Staff"
                      rightSectionPointerEvents="none"
                      data={staffOptions}
                      value={form.values.staffIds}
                      onChange={(val) => form.setFieldValue("staffIds", val)}
                    />
                  </Grid.Col>
                )}
                <Grid.Col span={6}>
                  <Select
                    size="sm"
                    className={classes.inputField}
                    label="Shift"
                    placeholder="Select Shift"
                    rightSectionPointerEvents="none"
                    rightSection={<GoChevronDown color="#94A3B8" size={16} />}
                    clearable
                    data={[
                      { value: "all", label: "All" },
                      { value: "morning", label: "Morning" },
                      { value: "evening", label: "Evening" },
                      { value: "night", label: "Night" },
                    ]}
                    {...form.getInputProps("shift")}
                  />
                </Grid.Col>
              </Grid>
            </Box>

            {/* ── Period ────────────────────────────────────────────────────── */}
            <Box className={classes.sectionCard}>
              <Flex align="center" gap={8} className={classes.sectionHeader}>
                <LuCalendarDays size={16} />
                <Text>Period</Text>
              </Flex>
              <Text className={classes.fieldLabel}>
                Date range <span style={{ color: "red" }}>*</span>
              </Text>
              <DateRangePicker
                dateRange={dateRange}
                setDateRange={setDateRange}
                handleApply={handleApply}
                open={open}
                opened={opened}
                close={close}
                size="md"
                mt={20}
                style={{ marginTop: "20px" }}
              />
              {customError.period && (
                <Text color="red" fz={16} mt={4}>
                  {customError.period}
                </Text>
              )}
            </Box>

            {/* ── Advanced trigger ──────────────────────────────────────────── */}
            <Flex justify="flex-end">
              <UnstyledButton
                type="button"
                onClick={toggleAdv}
                className={cx(classes.advTrigger, advOpened && classes.advTriggerActive)}
              >
                <Text>Advanced</Text>
                <LuSettings size={15} />
              </UnstyledButton>
            </Flex>

            {/* ── Advanced options ──────────────────────────────────────────── */}
            <Collapse in={advOpened}>
              <Box className={classes.sectionCard}>
                <Flex align="center" justify="space-between" className={classes.sectionHeader}>
                  <Flex align="center" gap={8}>
                    <LuFileText size={16} />
                    <Text>Advanced options</Text>
                  </Flex>
                  <UnstyledButton type="button" onClick={closeAdv} className={classes.advCloseBtn}>
                    <LuX size={16} />
                  </UnstyledButton>
                </Flex>
                <Grid gutter={24}>
                  <Grid.Col span={12}>
                    <TextInput
                      size="sm"
                      className={classes.inputField}
                      label={
                        <>
                          Report title{" "}
                          <span className={classes.optionalLbl}>(optional)</span>
                        </>
                      }
                      placeholder="e.g. Sunrise Group Home, July Progress Notes"
                      {...form.getInputProps("reportTitle")}
                    />
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Select
                      size="sm"
                      className={classes.inputField}
                      label="Sort order"
                      data={[
                        { value: "newest", label: "Newest first" },
                        { value: "oldest", label: "Oldest first" },
                      ]}
                      rightSectionPointerEvents="none"
                      rightSection={<GoChevronDown color="#94A3B8" size={16} />}
                      {...form.getInputProps("sortOrder")}
                    />
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Text className={classes.fieldLabel}>
                      Include staff signatures
                    </Text>
                    <OffOnToggle
                      value={form.values.includeSignatures}
                      onChange={(v) => form.setFieldValue("includeSignatures", v)}
                    />
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Text className={classes.fieldLabel}>
                      Include approval trail
                    </Text>
                    <OffOnToggle
                      value={form.values.includeApprovalTrail}
                      onChange={(v) => form.setFieldValue("includeApprovalTrail", v)}
                    />
                  </Grid.Col>
                </Grid>
              </Box>
            </Collapse>

            {customError.notFound && (
              <Box className={classes.emptyResultAlert}>
                <LuInfo size={16} />
                <Text>{customError.notFound}</Text>
              </Box>
            )}
          </>
        )}

        {/* ── Report Preview — the real generated PDF(s), rendered inline ────── */}
        {step === "preview" && (
          <Box>
            {generatedFiles.map((f) => (
              <Box key={f.filename} mb={16}>
                {/* {generatedFiles.length > 1 && (
                  <Flex align="center" gap={8} className={classes.sectionHeader}>
                    <LuFileText size={16} />
                    <Text>{f.label}</Text>
                  </Flex>
                )} */}
                {/* Every note type (Incident Report included) previews through
                    ReportPreviewPane; the raw PDF <embed> is only a fallback for
                    when the preview payload is missing. */}
                {f.previewKind === "custom" && f.previewData?.cover ? (
                  <ReportPreviewPane cover={f.previewData.cover} entries={f.previewData.entries} />
                ) : (
                  <Box className={classes.sectionCard}>
                    <embed
                      src={`${f.url}#toolbar=0&navpanes=0&statusbar=0&messages=0&view=FitH`}
                      type="application/pdf"
                      className={classes.previewFrame}
                    />
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        )}

        {/* ── Report Ready — confirmation + re-download ──────────────────────── */}
        {step === "done" && (
          <Box className={classes.doneWrap}>
            <Box className={classes.doneIcon}>
              <LuCheck size={28} />
            </Box>
            <Text className={classes.doneTitle}>Report ready</Text>
            <Text className={classes.doneSub}>
              {noteCount} note entries · {period[0]?.toLocaleDateString()} -{" "}
              {period[1]?.toLocaleDateString()}
            </Text>
            {generatedFiles.map((f) => (
              <Flex key={f.filename} align="center" gap={12} className={classes.fileChip}>
                <Box className={classes.fileIco}>PDF</Box>
                <Box style={{ flex: 1, minWidth: 0 }}>
                  <Text className={classes.fileName}>{f.label}</Text>
                  <Text className={classes.fileMeta} title={f.filename}>
                    PDF document · {(f.blob.size / 1024 / 1024).toFixed(1)} MB
                  </Text>
                </Box>
              </Flex>
            ))}
            <Button
              className={classes.createBtn}
              onClick={handleDownloadAgain}
              style={{ minWidth: 220, justifyContent: "center" }}
            >
              <LuDownload size={15} style={{ marginRight: 6 }} />
              {downloadedAgain ? "Downloaded" : "Download again"}
            </Button>
            <Box mt={12}>
              <UnstyledButton
                type="button"
                onClick={handleCreateAnother}
                className={classes.selectAllLink}
              >
                Create another report
              </UnstyledButton>
            </Box>
          </Box>
        )}
        </Box>

        {/* ── Persistent footer — always visible, never scrolls with content ── */}
        <Flex
          gap={7}
          align="center"
          justify={step === "form" || step === "preview" ? "space-between" : "flex-end"}
          className={classes.drawerFooter}
        >
          {step === "form" && (
            <>
              <Button className={classes.cancelBtn} onClick={() => resetAndClose()}>
                Cancel
              </Button>
              <Button
                disabled={!isFormValid || pdfGenerating}
                type="submit"
                className={classes.createBtn}
                loading={pdfGenerating}
              >
                {pdfGenerating ? "Generating PDF..." : "Generate report"}
              </Button>
            </>
          )}
          {step === "preview" && (
            <>
              <Button className={classes.cancelBtnPrev} onClick={resetAndClose} type="button">
                Close
              </Button>
              <Button className={classes.createBtnPrev} onClick={handleDownload} type="button">
                <LuDownload size={15} style={{ marginRight: 6 }} />
                Download PDF
              </Button>
            </>
          )}
          {step === "done" && (
            <Button className={classes.cancelBtn} onClick={resetAndClose} type="button">
              Close
            </Button>
          )}
        </Flex>
      </form>
    </Drawer>
  );
};

export default NoteReportDrawer;
